import { Package, ArrowUpRight, ArrowDownRight, Users, ChevronRight } from 'lucide-react'
import { cn, formatPrice, stripBrandPrefixFromProductName } from '@/lib/utils'
import type { ProductCompetitivePosition, CompetitorPresenceLevel } from '@/types/competition'

interface ProductCompetitiveCardProps {
    product: ProductCompetitivePosition
    isSelected: boolean
    showCompetitors: boolean
    onSelect: () => void
    onToggleCompetitors: () => void
}

const presenceConfig: Record<CompetitorPresenceLevel, { color: string; bg: string }> = {
    'Low': { color: 'text-emerald-700 dark:text-emerald-400', bg: 'bg-emerald-500/10' },
    'Medium': { color: 'text-amber-700 dark:text-amber-400', bg: 'bg-amber-500/10' },
    'High': { color: 'text-orange-700 dark:text-orange-400', bg: 'bg-orange-500/10' },
    'Very High': { color: 'text-red-700 dark:text-red-400', bg: 'bg-red-500/10' },
}

export function ProductCompetitiveCard({
    product,
    isSelected,
    onSelect,
}: ProductCompetitiveCardProps) {
    const priceDiff = product.myPrice - product.competitorPrice
    const isPriceHigher = priceDiff > 0
    const priceDiffPercent = product.competitorPrice > 0
        ? Math.abs((priceDiff / product.competitorPrice) * 100).toFixed(1)
        : '0'
    const presence = presenceConfig[product.competitorPresence]

    return (
        <div
            className={cn(
                // Glass surface
                "group/card relative rounded-2xl overflow-hidden cursor-pointer",
                "bg-white/60 dark:bg-white/[0.08]",
                "backdrop-blur-xl backdrop-saturate-[1.4]",
                // Glass edge
                "border border-white/50 dark:border-white/[0.12]",
                "shadow-[0_2px_16px_rgba(0,0,0,0.06),inset_0_1px_0_0_rgba(255,255,255,0.6)]",
                "dark:shadow-[0_2px_16px_rgba(0,0,0,0.3),inset_0_1px_0_0_rgba(255,255,255,0.08)]",
                // Transitions
                "transition-shadow duration-200 ease-out",
                "hover:shadow-[0_8px_30px_rgba(0,0,0,0.1),inset_0_1px_0_0_rgba(255,255,255,0.7)]",
                "hover:border-primary/20",
                // Selected
                isSelected && [
                    "ring-[1.5px] ring-primary/50",
                    "bg-white/70 dark:bg-white/[0.12]",
                    "shadow-[0_8px_30px_rgba(0,0,0,0.1),inset_0_1px_0_0_rgba(255,255,255,0.7)]",
                ]
            )}
            onClick={onSelect}
        >
            {/* Blurred product image in background */}
            {product.imageUrl && (
                <img
                    src={product.imageUrl}
                    alt=""
                    aria-hidden
                    className="absolute -bottom-6 -right-6 w-40 h-40 object-contain opacity-[0.07] blur-lg scale-110 pointer-events-none select-none"
                    referrerPolicy="no-referrer"
                    loading="lazy"
                />
            )}

            {/* Content */}
            <div className="relative z-[1]">
                {/* Product header */}
                <div className="p-3 pb-2">
                    <div className="flex gap-3 items-start">
                        {/* Image */}
                        <div className="w-11 h-11 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center bg-white/70 dark:bg-white/10 border border-white/60 dark:border-white/10">
                            {product.imageUrl ? (
                                <img
                                    src={product.imageUrl}
                                    alt={product.name}
                                    className="w-full h-full object-contain scale-125"
                                    referrerPolicy="no-referrer"
                                    loading="lazy"
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).style.display = 'none'
                                    }}
                                />
                            ) : (
                                <Package className="h-5 w-5 text-muted-foreground" />
                            )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                            <h3 className="module-product-title" title={product.name}>
                                {stripBrandPrefixFromProductName(product.name, product.variantSize)}
                            </h3>
                            <p className="module-product-subtitle mt-0.5 truncate">
                                {product.variantSize}
                            </p>
                        </div>

                        {/* Presence pill */}
                        <div className={cn(
                            "flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold flex-shrink-0",
                            presence.bg, presence.color
                        )}>
                            <Users className="h-2.5 w-2.5" />
                            {product.competitorPresence}
                        </div>
                    </div>
                </div>

                {/* Glass divider */}
                <div className="mx-3 h-px bg-gradient-to-r from-transparent via-black/[0.06] dark:via-white/[0.08] to-transparent" />

                {/* Pricing */}
                <div className="p-3 pt-2">
                    <div className="flex items-stretch gap-2">
                        {/* Your price */}
                        <div className="flex-1 rounded-xl bg-white/40 dark:bg-white/[0.06] border border-white/40 dark:border-white/[0.08] p-2 text-center">
                            <span className="module-metric-kicker block mb-1">You</span>
                            <span className={cn(
                                "module-metric-value",
                                isPriceHigher ? "text-red-600 dark:text-red-400" : "text-foreground"
                            )}>
                                {product.myPrice > 0 ? `₹${formatPrice(product.myPrice)}` : '—'}
                            </span>
                        </div>

                        {/* vs */}
                        <div className="flex items-center">
                            <span className="text-xs font-bold text-muted-foreground/60">vs</span>
                        </div>

                        {/* Comp price */}
                        <div className="flex-1 rounded-xl bg-white/40 dark:bg-white/[0.06] border border-white/40 dark:border-white/[0.08] p-2 text-center">
                            <span className="module-metric-kicker block mb-1">Comp</span>
                            <span className="module-metric-value">
                                {product.competitorPrice > 0 ? `₹${formatPrice(product.competitorPrice)}` : '—'}
                            </span>
                        </div>
                    </div>

                    {/* Price diff badge */}
                    {product.myPrice > 0 && product.competitorPrice > 0 && (
                        <div className={cn(
                            "flex items-center justify-center gap-1 mt-2 py-1.5 rounded-lg text-xs font-semibold",
                            isPriceHigher
                                ? "bg-red-500/10 text-red-700 dark:text-red-400"
                                : "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                        )}>
                            {isPriceHigher ? (
                                <ArrowUpRight className="h-3.5 w-3.5" />
                            ) : (
                                <ArrowDownRight className="h-3.5 w-3.5" />
                            )}
                            {priceDiffPercent}% {isPriceHigher ? 'above' : 'below'}
                        </div>
                    )}
                </div>

                {/* Drill-down footer */}
                <div className={cn(
                    "group/footer mx-3 mb-2.5 mt-0.5 flex items-center justify-between px-2.5 py-1.5 rounded-xl relative overflow-hidden",
                    "bg-primary/[0.04] dark:bg-primary/[0.08]",
                    "border border-primary/10 dark:border-primary/15",
                    "transition-all duration-300 ease-out",
                    // Card hover
                    "group-hover/card:bg-primary/[0.07] group-hover/card:border-primary/20",
                    // Footer direct hover
                    "hover:!bg-primary/[0.14] hover:!border-primary/30 hover:shadow-[0_0_12px_rgba(var(--primary-rgb,99,102,241),0.15)]",
                    isSelected && "bg-primary/10 border-primary/25"
                )}>
                    {/* Sliding highlight on hover */}
                    <div className="absolute inset-0 -translate-x-full group-hover/footer:translate-x-0 bg-gradient-to-r from-primary/[0.06] via-primary/[0.1] to-transparent transition-transform duration-500 ease-out pointer-events-none" />

                    <span className={cn(
                        "relative text-xs font-semibold uppercase tracking-wider",
                        "text-muted-foreground/70 transition-all duration-300",
                        "group-hover/card:text-primary/80",
                        "group-hover/footer:!text-primary group-hover/footer:tracking-[0.1em]",
                        isSelected && "text-primary"
                    )}>
                        View Competitors
                    </span>
                    <div className="relative flex items-center gap-0.5">
                        {/* Double chevron effect on hover */}
                        <ChevronRight className={cn(
                            "h-3.5 w-3.5 transition-all duration-300",
                            "text-muted-foreground/50",
                            "group-hover/card:text-primary/60",
                            "group-hover/footer:!text-primary group-hover/footer:translate-x-0.5",
                            isSelected && "text-primary translate-x-0.5"
                        )} />
                        <ChevronRight className={cn(
                            "h-3.5 w-3.5 -ml-2 transition-all duration-300",
                            "text-transparent",
                            "group-hover/footer:!text-primary/40 group-hover/footer:-ml-1.5 group-hover/footer:translate-x-0.5",
                            isSelected && "text-primary/30 -ml-1.5"
                        )} />
                    </div>
                </div>
            </div>
        </div>
    )
}
